/**
 * The demo narrator's look — one dark palette, defined once, for every piece of
 * floating demo chrome (the first-run rail, the guided tour's dialogs, the
 * welcome banner, the feedback pill, the "What if?" card).
 *
 * WHY DARK. The app is light-only. A prospect walking a demo meets two kinds of
 * surface — the product, and the thing narrating the product — and until now
 * both were the same off-white popover. Filmed feedback from a real walkthrough:
 * "demo overlays must look DIFFERENT from the main app... so the user can
 * distinguish demo overlay from app elements." So the narrator commits to dark.
 * It is not a theme toggle and not `dark:` variants: the app has no dark mode to
 * inherit from, so every value here is EXPLICIT and unconditional.
 *
 * HOW IT IS APPLIED. `demoChromeVars` sets the `--demo-*` custom properties as
 * an inline style on each chrome root; everything below (and the arbitrary-value
 * classes in the components) reads them through `var(--demo-…)`. That keeps one
 * definition of every colour while still allowing hover/focus states, which
 * inline styles cannot express. Setting the vars inline rather than on a global
 * class also means the chrome carries its palette into portals — the rail and
 * the tour both render into `document.body`, outside any app wrapper.
 *
 * THE ONE EXCEPTION: the file-drop zone stays LIGHT. It is an input surface of
 * the APP (the prospect's own file goes in there), not narration, so it keeps
 * the app's palette and takes only the micro-badge (`DEMO_BADGE_LIGHT`).
 *
 * CONTRAST (WCAG 2.1 relative luminance, computed by hand — every pair a human
 * reads as text clears 4.5:1):
 *
 *   L(#f4f1ec) = 0.8819   L(#1c1917) = 0.0111   L(#262220) = 0.0166
 *   L(#a8a199) = 0.3612   L(#e0651f) = 0.2548   L(#14100e) = 0.0054
 *   L(#7bc492) = 0.4576   L(#ef8892) = 0.3803
 *
 *   text    #f4f1ec on surface #1c1917 → (0.8819+0.05)/(0.0111+0.05) = 15.3:1
 *   text    #f4f1ec on raised  #262220 → (0.8819+0.05)/(0.0166+0.05) = 14.0:1
 *   muted   #a8a199 on surface #1c1917 → (0.3612+0.05)/(0.0111+0.05) =  6.7:1
 *   muted   #a8a199 on raised  #262220 → (0.3612+0.05)/(0.0166+0.05) =  6.2:1
 *   accent  #e0651f on surface #1c1917 → (0.2548+0.05)/(0.0111+0.05) =  5.0:1
 *   accent  #e0651f on raised  #262220 → (0.2548+0.05)/(0.0166+0.05) =  4.6:1
 *   posit.  #7bc492 on surface #1c1917 → (0.4576+0.05)/(0.0111+0.05) =  8.3:1
 *   posit.  #7bc492 on raised  #262220 → (0.4576+0.05)/(0.0166+0.05) =  7.6:1
 *   negat.  #ef8892 on surface #1c1917 → (0.3803+0.05)/(0.0111+0.05) =  7.0:1
 *   negat.  #ef8892 on raised  #262220 → (0.3803+0.05)/(0.0166+0.05) =  6.5:1
 *
 * The positive/negative pair is red-green, so it never carries meaning ALONE:
 * every delta it colours is a server-formatted string that already states its
 * own direction ("+1.2% vs plan", "-120bps"), and the colour is the second
 * channel. The negative is a rose (hue ~353°) rather than a red-orange, so it
 * cannot be read as the brand accent (~23°) at chip size.
 *
 * The primary button is the pair worth explaining. White on the brand orange is
 * 2.96:1 — the app ships that (`variant="brand"`), and on a light page beside
 * dark ink it passes as a control rather than as reading matter. Here the
 * narrator IS the reading matter, so the fill keeps the brand orange (the brief
 * is explicit: the accent stays #e0651f) and the LABEL goes near-black instead:
 *
 *   ink #14100e on accent #e0651f → (0.2548+0.05)/(0.0054+0.05) = 5.5:1
 *
 * which is the only way to clear AA on this hue without darkening the fill.
 */

import type { CSSProperties } from "react";

/** The raw values. Exported so a component that must compute a colour (a
 *  gradient, a canvas, an inline SVG stroke) reads the same numbers the
 *  classes below do, rather than re-typing a hex. */
export const DEMO_CHROME = {
  /** Warm near-black. The narrator's ground. */
  surface: "#1c1917",
  /** One step up — cards, log wells, hover states. */
  raised: "#262220",
  /** Body text. */
  text: "#f4f1ec",
  /** Secondary text: eyebrows, captions, timings, the micro-badge. */
  muted: "#a8a199",
  /** Hairline. Deliberately a white alpha, so it reads as a lit edge on both
   *  the surface and the raised surface without a second value. */
  border: "rgba(255, 255, 255, 0.12)",
  /** A brighter hairline for the raised card sitting on the tour's dimmer,
   *  which is nearly the same value as the surface itself. */
  borderStrong: "rgba(255, 255, 255, 0.22)",
  /** The brand orange. Unchanged from the app — it is what pops on dark. */
  accent: "#e0651f",
  /** The accent at low alpha: chip fills, selected rows. */
  accentSoft: "rgba(224, 101, 31, 0.16)",
  /** Label colour ON the accent fill (see the contrast note above). */
  accentInk: "#14100e",
  /** Focus ring: the accent at 50%, per the brief. */
  ring: "rgba(224, 101, 31, 0.5)",
  /** A delta that is good news. Restrained — a demo that shouts every number
   *  in green is a dashboard, not a finding. */
  positive: "#7bc492",
  /** A delta that is bad news. A rose, so it is never confused with the brand
   *  accent, and never the only signal (the delta string states its sign). */
  negative: "#ef8892",
} as const;

/**
 * Inline style that publishes the palette to a chrome root and its descendants.
 * Every dark surface starts with `style={demoChromeVars}`.
 */
export const demoChromeVars = {
  "--demo-surface": DEMO_CHROME.surface,
  "--demo-raised": DEMO_CHROME.raised,
  "--demo-text": DEMO_CHROME.text,
  "--demo-muted": DEMO_CHROME.muted,
  "--demo-border": DEMO_CHROME.border,
  "--demo-border-strong": DEMO_CHROME.borderStrong,
  "--demo-accent": DEMO_CHROME.accent,
  "--demo-accent-soft": DEMO_CHROME.accentSoft,
  "--demo-accent-ink": DEMO_CHROME.accentInk,
  "--demo-ring": DEMO_CHROME.ring,
  "--demo-positive": DEMO_CHROME.positive,
  "--demo-negative": DEMO_CHROME.negative,
} as CSSProperties;

// ── ready-made class strings ─────────────────────────────────────────────────
// Literals, so Tailwind's scanner sees them (styles.css sources `./**/*.ts`).

/** Focus treatment for every interactive element on the dark chrome. The app's
 *  `ring-ring/50` is tuned for a light ground and vanishes here. */
export const DEMO_FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--demo-ring)] focus-visible:border-[var(--demo-accent)]";

/** The narrator's panel: dark ground, hairline, hard shadow. */
export const DEMO_PANEL =
  "rounded-xl border border-[var(--demo-border-strong)] bg-[var(--demo-surface)] text-[var(--demo-text)] shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)]";

/**
 * The micro-badge — "DEMO GUIDE" in 10px caps. Tonal difference alone is a
 * design claim; this is the explicit one, so a prospect never has to infer
 * which surface is talking.
 */
export const DEMO_BADGE =
  "inline-flex items-center rounded-[3px] border border-[var(--demo-border)] px-1.5 py-px text-[10px] leading-[1.5] font-semibold tracking-[0.14em] text-[var(--demo-muted)] uppercase";

/** Same badge for the ONE surface that stays light (the file-drop zone). */
export const DEMO_BADGE_LIGHT =
  "inline-flex items-center rounded-[3px] border border-[var(--color-stone,#d9d3c9)] px-1.5 py-px text-[10px] leading-[1.5] font-semibold tracking-[0.14em] text-[var(--color-steel,#6b6660)] uppercase";

/** State eyebrow (the panel header's left-hand label). */
export const DEMO_EYEBROW =
  "text-[11px] font-medium tracking-[0.08em] text-[var(--demo-muted)] uppercase";

/** Secondary copy. */
export const DEMO_MUTED_TEXT = "text-[var(--demo-muted)]";

/** A pickable card (the choose state's workflow cards). */
export const DEMO_CARD = `rounded-lg border border-[var(--demo-border)] bg-[var(--demo-raised)] p-3 text-left transition-colors hover:border-[var(--demo-accent)] hover:bg-[#2f2a26] ${DEMO_FOCUS}`;

/** A static chip (promise pages, file names, reveal page links). */
export const DEMO_CHIP =
  "rounded-md border border-[var(--demo-border)] bg-[var(--demo-raised)] px-2 py-1 text-[12px] text-[var(--demo-text)]/85";

/** A clickable chip (the reveal state's page jumps). */
export const DEMO_CHIP_BUTTON = `rounded-md border border-[var(--demo-border)] bg-[var(--demo-raised)] px-2.5 py-1 text-[12px] text-[var(--demo-text)] transition-colors hover:border-[var(--demo-accent)] hover:bg-[#2f2a26] ${DEMO_FOCUS}`;

/** The same chip, one size down — for a row of page jumps that is no longer
 *  the point of its screen (the reveal, once the outcome card leads it). */
export const DEMO_CHIP_BUTTON_SM = `rounded-md border border-[var(--demo-border)] bg-[var(--demo-raised)] px-2 py-0.5 text-[11px] text-[var(--demo-text)]/90 transition-colors hover:border-[var(--demo-accent)] hover:bg-[#2f2a26] ${DEMO_FOCUS}`;

/** The "builds it live" mark. Orange border + orange text on dark (4.6:1 on the
 *  raised card it sits inside) — re-contrasted from the light chip, which used
 *  a 10%-tint fill that disappears here. */
export const DEMO_LIVE_CHIP =
  "mb-1.5 inline-flex items-center rounded-full border border-[var(--demo-accent)] bg-[var(--demo-accent-soft)] px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[var(--demo-accent)] uppercase";

/** The quieter sibling of DEMO_LIVE_CHIP: a card whose moment is a recorded
 *  run rather than a live build. Same shape so the cards read as one set;
 *  muted rather than accent so the live card still leads. */
export const DEMO_RECORDED_CHIP =
  "mb-1.5 inline-flex items-center rounded-full border border-[var(--demo-border)] px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[var(--demo-muted)] uppercase";

/** A raised well for a block the narrator is QUOTING rather than saying: the
 *  reveal's outcome card, the repeat beat's workflow card. Raised, so the
 *  numbers inside read as a finding and not as more narration. */
export const DEMO_WELL =
  "rounded-lg border border-[var(--demo-border)] bg-[var(--demo-raised)] px-3.5 py-3";

/** Delta colour by sentiment. A LOOKUP of literal class strings, not a
 *  template — Tailwind's scanner reads this file as source, and a class
 *  assembled at runtime would never be generated. `neutral` is the fallback
 *  for anything the server didn't classify. */
export const DEMO_DELTA: Record<"good" | "bad" | "neutral", string> = {
  good: "text-[var(--demo-positive)]",
  bad: "text-[var(--demo-negative)]",
  neutral: "text-[var(--demo-muted)]",
};

/** Primary action: brand-orange fill, near-black label (5.5:1). */
export const DEMO_PRIMARY_BTN = `inline-flex h-7 shrink-0 items-center justify-center rounded-md bg-[var(--demo-accent)] px-2.5 text-[0.8rem] font-semibold text-[var(--demo-accent-ink)] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 ${DEMO_FOCUS}`;

/** Secondary action: hairline on dark, lifts to the raised surface on hover. */
export const DEMO_GHOST_BTN = `inline-flex h-7 shrink-0 items-center justify-center rounded-md border border-[var(--demo-border)] bg-transparent px-2.5 text-[0.8rem] font-medium text-[var(--demo-text)] transition-colors hover:bg-[var(--demo-raised)] disabled:pointer-events-none disabled:opacity-50 ${DEMO_FOCUS}`;

/** Tertiary: the "Skip" / "All sections" text affordance. */
export const DEMO_SUBTLE_BTN = `rounded-md px-1.5 py-0.5 text-[12px] text-[var(--demo-muted)] transition-colors hover:text-[var(--demo-text)] ${DEMO_FOCUS}`;

/** The provisioning / build log well. */
export const DEMO_LOG =
  "overflow-y-auto rounded-lg border border-[var(--demo-border)] bg-[#151312] p-3 font-mono text-[12px] leading-[1.75]";

/** The tour's serif body voice, recoloured for dark. The face is unchanged —
 *  it is what makes the tour read as a guide rather than another panel. */
export const DEMO_SERIF_BODY = {
  fontFamily: "Charter, 'Iowan Old Style', Georgia, 'Times New Roman', serif",
  fontSize: 15,
  lineHeight: 1.65,
  color: DEMO_CHROME.text,
} as CSSProperties;
